import i18n, { LOCALES, type Locale } from "@/i18n"
import type { ProjectTypeKey } from "@/lib/project-types"

type OverlayBundle = Record<string, Record<string, unknown>>

const overlayModules = import.meta.glob<{ default: OverlayBundle }>("../locales/project-type-overrides/*/*.json", {
  eager: true,
})

const overlaysByType: Partial<Record<ProjectTypeKey, Partial<Record<Locale, OverlayBundle>>>> = {}

for (const [path, mod] of Object.entries(overlayModules)) {
  const match = path.match(/project-type-overrides\/([^/]+)\/(en|ru)\.json$/)
  if (!match) continue
  const [, type, lng] = match
  const key = type as ProjectTypeKey
  ;(overlaysByType[key] ??= {})[lng as Locale] = mod.default
}

const baseSnapshots = new Map<string, Record<string, unknown>>()

function snapshotKey(lng: string, ns: string) {
  return `${lng}:${ns}`
}

function ensureBaseSnapshot(lng: string, ns: string) {
  const key = snapshotKey(lng, ns)
  if (baseSnapshots.has(key)) return
  const current = i18n.getResourceBundle(lng, ns)
  if (current && typeof current === "object") {
    baseSnapshots.set(key, structuredClone(current) as Record<string, unknown>)
  }
}

function deepMerge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...target }
  for (const [key, value] of Object.entries(source)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const existing = out[key]
      out[key] =
        existing && typeof existing === "object" && !Array.isArray(existing)
          ? deepMerge(existing as Record<string, unknown>, value as Record<string, unknown>)
          : value
    } else {
      out[key] = value
    }
  }
  return out
}

let appliedType: ProjectTypeKey | null = null
const touchedKeys = new Set<string>()

/** Force react-i18next subscribers to re-render after resource bundles change. */
function notifyI18nResourcesChanged() {
  void i18n.changeLanguage(i18n.language)
}

export function clearProjectTypeI18n() {
  if (touchedKeys.size === 0) {
    appliedType = null
    return
  }

  for (const key of touchedKeys) {
    const [lng, ns] = key.split(":")
    const base = baseSnapshots.get(key)
    if (base) {
      i18n.addResourceBundle(lng, ns, structuredClone(base), true, true)
    }
  }
  touchedKeys.clear()
  appliedType = null
  notifyI18nResourcesChanged()
}

export function applyProjectTypeI18n(type: ProjectTypeKey | null | undefined) {
  const next = type && type !== "serial" ? type : null
  if (appliedType === next) return

  clearProjectTypeI18n()
  if (!next) return

  for (const lng of LOCALES) {
    const bundle = overlaysByType[next]?.[lng]
    if (!bundle) continue
    for (const [ns, patch] of Object.entries(bundle)) {
      ensureBaseSnapshot(lng, ns)
      const key = snapshotKey(lng, ns)
      const base = baseSnapshots.get(key) ?? {}
      const merged = deepMerge(structuredClone(base), patch as Record<string, unknown>)
      i18n.addResourceBundle(lng, ns, merged, true, true)
      touchedKeys.add(key)
    }
  }
  appliedType = next
  notifyI18nResourcesChanged()
}
